<script lang="ts" setup>
import FilterTable from "@/components/Common/Table/FilterTable.vue";
import { ref } from "vue";
import type { Header } from "vue3-easy-data-table";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import Swal from "sweetalert2";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import { useAccessPermissionStore } from "@/stores/access-permission";
import { hasAccess } from "@/utils/helper";

const linkAdd = {
  text: "Add Access Permission",
  url: "/master/access-permission/create",
};

const headers: Header[] = [
  { text: "CODE", value: "code" },
  { text: "NAME", value: "name" },
  { text: "ACTION", value: "action" },
  { text: "ACTIONS", value: "actions" },
];

const page = ref<number>(1);
const limit = ref<number>(10);
const totalItems = ref<number>(0);
const listAccessPermission = ref<any>([]);
const isLoading = ref(false);
const accessPermissionStore = useAccessPermissionStore();

const loadDataAccessPermission = async (query = "") => {
  isLoading.value = true;
  const { success, message } = await accessPermissionStore.dispatchListAll(
    page.value,
    limit.value,
    query
  );
  if (success) {
    listAccessPermission.value = await accessPermissionStore.listAccess.items;
    page.value = await accessPermissionStore.listAccess.page;
    limit.value = await accessPermissionStore.listAccess.pageSize;
    totalItems.value = await accessPermissionStore.listAccess.total;
  }
  isLoading.value = false;
};

const handleServerOptionsUpdate = (options: {
  page: number;
  rowsPerPage: number;
}) => {
  page.value = options.page;
  limit.value = options.rowsPerPage;
  loadDataAccessPermission();
};

const handleEdit = (role: any) => {
  router.push("/master/access-permission/" + role);
};

const handleDelete = async (accessPermissionData: any) => {
  const swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      cancelButton: "btn btn-danger",
      confirmButton: "btn btn-success",
    },
    buttonsStyling: false,
  });
  swalWithBootstrapButtons
    .fire({
      title: `Are you sure want to delete data ${accessPermissionData.name} ?`,
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    })
    .then(async (result) => {
      if (result.isConfirmed) {
        const { success, message } = await accessPermissionStore.dispatchDelete(
          accessPermissionData.id
        );
        if (success) {
          swalWithBootstrapButtons.fire({
            title: "Deleted!",
            text: "Your file has been deleted.",
            icon: "success",
          });
          loadDataAccessPermission();
        } else {
          toast.error(message);
        }
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        swalWithBootstrapButtons.fire({
          title: "Cancelled",
          text: "Your action has been aborted",
          icon: "error",
        });
      }
    });
};

loadDataAccessPermission();
</script>
<template>
  <div class="card bg-white border-0 rounded-3 mb-4">
    <div class="card-body p-4">
      <div
        class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
      >
        <h3 class="mb-0">List Data Access Permission</h3>
      </div>
      <FilterTable :link="linkAdd" @searchByQuery="loadDataAccessPermission" />
      <div class="table-content">
        <BaseLoading v-if="isLoading" />
        <EasyDataTable
          :headers="headers"
          :items="listAccessPermission"
          v-if="!isLoading"
          show-index
          :server-options="{
            page: page,
            rowsPerPage: limit,
          }"
          :server-items-length="totalItems"
          @update:server-options="handleServerOptionsUpdate"
          buttons-pagination
        >
          <template #item-action="props">
            <div v-if="props.action.length > 0">
              <span v-for="item in props.action" :key="item.id"
                >{{ item.name }},
              </span>
            </div>
          </template>
          <template #item-actions="props">
            <div class="d-flex gap-2">
              <button
                v-if="hasAccess('access_permission-update')"
                class="btn btn-info btn-sm text-white"
                @click="handleEdit(props.id)"
              >
                Edit
              </button>
              <button
                v-if="hasAccess('access_permission-delete')"
                class="btn btn-danger btn-sm"
                @click="handleDelete(props)"
              >
                Delete
              </button>
            </div>
          </template>
        </EasyDataTable>
      </div>
    </div>
  </div>
</template>
